import { CircleLoader } from "@exopengithub/exokit";
import {
	createBrowserRouter,
	Navigate,
	Outlet,
	data,
	isRouteErrorResponse,
	redirect,
	useRouteError,
	useParams,
	type RouteObject,
} from "react-router";
import { useAiChatAccess } from "../features/ai-chat/hooks/useAiChatAccess.js";
import { ProtectedApp } from "../ProtectedApp.js";
import { StartDashboard } from "../features/startDashboard/StartDashboard.js";
import { Reports } from "../features/ewrReports/EwrReports.js";
import { Home } from "../features/home/Home.js";
import { HomeSidebar } from "../features/sidebar/HomeSidebar.js";
import { AccessGroupTable } from "../features/usersAndGroups/AccessGroupTable.js";
import { UserTable } from "../features/usersAndGroups/UserTable.js";
import i18n from "i18next";
import { showErrorNotification } from "molecules/notifications/notificationEvents";
import { AuthRoute } from "./AuthRoute.js";
import { useLoadedCompanyDomain } from "./routeDataHooks.js";
import { CompanyRouteLayout } from "./CompanyRouteLayout.js";
import { AppFrame, MainFrame } from "./ProtectedRoutes.js";
import { lazy } from "../common/utils/lazy.js";
import { PlanPageWrapper } from "../features/planning/PlanPageWrapper.js";
import { GroupHierarchyCard } from "../features/data-perspectives/legal-entities/GroupHierarchyCard.js";
import { BalanceSheetHierarchyPage } from "../features/data-perspectives/pages/BalanceSheetPage.js";
import { CostCenterHierarchyPage } from "../features/data-perspectives/pages/CostCenterHierarchyPage.js";
import { CustomerHierarchyPage } from "../features/data-perspectives/pages/CustomerHierarchyPage.js";
import { PlanningUsersPage } from "../features/planning/settings/users/PlanningUsersPage.js";
import { ConnectIntegrationPage } from "../features/integrations/setup/ConnectIntegrationPage.js";
import { AddVismaNetIntegrationRedirect } from "../features/integrations/sources/visma/AddVismaNetIntegrationRedirect.js";
import { ActivateFortnoxPage } from "../features/integrations/sources/fortnox/ActivateFortnoxPage.js";
import { AddFortnoxIntegrationExternalRedirect } from "../features/integrations/sources/fortnox/AddFortnoxIntegrationExternalRedirect.js";
import { ActivateExactOnlinePage } from "../features/integrations/sources/exact-online/ActivateExactOnlinePage.js";
import { AddExactOnlineIntegrationExternalRedirect } from "../features/integrations/sources/exact-online/AddExactOnlineIntegrationExternalRedirect.js";
import { ExactOnlineExternalCallbackPage } from "../features/integrations/sources/exact-online/ExactOnlineExternalCallbackPage.js";
import { Documentation } from "../features/integrations/overview/Documentation.js";
import { IncomingBalancePage } from "../features/planning/incoming-balance/IncomingBalancePage.js";
import { GeneralSettingsPage } from "../features/planning/settings/GeneralSettingsPage.js";
import { PLANNING_DEFINITIONS_TAB_ID } from "./routerConstants.js";
import { SettingsPage } from "../features/settings/SettingsPage.js";
import { CompanyDomainSettingsPage } from "../features/settings/CompanyDomainSettingsPage.js";
import { TermsAndPoliciesPage } from "../features/settings/TermsAndPoliciesPage.js";
import { ApiKeysSettingsPage } from "../features/settings/apiKeys/ApiKeysSettingsPage.js";
import { AiChatSettingsPage } from "../features/settings/AiChatSettingsPage.js";
import { PlanningModulesSettingsPage } from "../features/settings/PlanningModulesSettingsPage.js";
import { FortnoxExternalCallbackPage } from "../features/integrations/sources/fortnox/FortnoxExternalCallbackPage.js";
import { PowerofficeExternalCallbackPage } from "../features/integrations/sources/poweroffice/PowerofficeExternalCallbackPage.js";
import { PageNotFound } from "src/features/appError/PageNotFound.js";
import { COMPANYADMIN, SUPERADMIN } from "src/common/service/users.js";
import { exopenApi } from "src/common/redux/api/exopenApi.js";
import { store } from "src/common/redux/store.js";
import { nexusApi } from "src/common/service/nexus/nexusApi.js";
import { userProfileApi } from "src/common/service/userProfile/userProfileApi.js";
import { isUserProfileServiceConfigured } from "src/env.js";
import {
	createLoaderWithAuthCheck,
	dataStrategy,
	dispatchUnloadEvents,
	ensureRoutesHaveLoaderFunction,
	subscribeToQuery,
} from "src/routes/routerUtils.js";

import { RouteTreeProvider } from "./routeTreeContext.js";
import { EmailVerification } from "src/features/emailVerification/EmailVerification.js";
import { fusionHubApi } from "src/common/service/fusionHub/fusionHubApi.js";
import { fortnoxApi } from "src/common/service/fusionHub/integrations/fortnoxApi.js";
import { planningApi } from "src/features/planning/api/planningApi.js";
import { LegalEntityDetailLayout } from "src/features/legalEntities/LegalEntityDetailLayout.js";
import { AccountHierarchyLayout } from "src/features/data-perspectives/pages/AccountHierarchyLayout.js";
import { LegalEntitiesPage } from "src/features/legalEntities/LegalEntitiesPage.js";
import { LegalEntitiesLayout } from "src/features/legalEntities/LegalEntitiesLayout.js";
import { InvoicesLayout } from "src/features/invoice/InvoicesLayout.js";
import { VismaEaccountingExternalCallbackPage } from "src/features/integrations/sources/visma-eaccounting/VismaEaccountingExternalCallbackPage.js";
import { VismaEconomicExternalCallbackPage } from "src/features/integrations/sources/visma-economic/VismaEconomicExternalCallbackPage.js";
import { VismaDineroExternalCallbackPage } from "src/features/integrations/sources/visma-dinero/VismaDineroExternalCallbackPage.js";
import { DatevExternalCallbackPage } from "src/features/integrations/sources/datev/DatevExternalCallbackPage.js";
import { isMissingOrForbidden } from "src/common/utils/httpErrors.ts";

const { ExcelReportsPage } = lazy(
	() => import("src/features/excelReports/ExcelReportsPage.js"),
);
const { PowerBiReportsPage } = lazy(
	() => import("src/features/powerBiReports/PowerBiReportsPage.js"),
);
const { DevToolsPage, DevToolsIndex, DevToolsSubPageLayout } = lazy(
	() => import("../features/devTools/DevTools.js"),
);
const { PermissionsPage } = lazy(
	() => import("../features/permissions/PermissionsPage.js"),
);
const { PermissionDefinitionsDevPage } = lazy(
	() => import("../features/permissions/PermissionDefinitionsDevPage.js"),
);
const { JobListingPage } = lazy(
	() => import("../features/integrations/jobs/JobListingPage.js"),
);
const { IntegrationDetailPage } = lazy(
	() => import("../features/integrations/overview/IntegrationDetailPage.js"),
);
const { JobDetailPage } = lazy(
	() => import("../features/integrations/jobs/JobDetailPage.js"),
);
const { SupportTools } = lazy(
	() => import("src/features/devTools/SupportTools.js"),
);
const { LegalEntities } = lazy(
	() => import("src/features/devTools/LegalEntities.js"),
);
const { PlanningAdminCP } = lazy(
	() => import("src/features/planning/PlanningAdminCP.js"),
);
const { AdminPlansPage } = lazy(
	() => import("../features/planning/admin/AdminPlansPage.js"),
);
const { AzureResourceTools } = lazy(
	() => import("src/features/devTools/AzureResourceTools.js"),
);
const { DLMTools } = lazy(() => import("src/features/devTools/DLMTools.js"));
const { TemplateApps } = lazy(
	() => import("src/features/devTools/TemplateApps.js"),
);
const { DevtoolIntegrationsPage } = lazy(
	() => import("src/features/devTools/DevtoolIntegrationsPage.js"),
);
const { NexusSyncsPage } = lazy(
	() => import("src/features/devTools/NexusSyncsPage.js"),
);
const { FleetIntegrationStatusPage } = lazy(
	() => import("src/features/devTools/FleetIntegrationStatusPage.js"),
);
const { InstanceMemoryPage } = lazy(
	() => import("src/features/devTools/InstanceMemoryPage.js"),
);
const { CustomerInvoicesPage } = lazy(
	() => import("src/features/invoice/CustomerInvoicesPage.js"),
);
const { SupplierInvoicesPage } = lazy(
	() => import("src/features/invoice/SupplierInvoicesPage.js"),
);
const { IncomeStatementHierarchyPage } = lazy(
	() => import("../features/data-perspectives/pages/IncomeStatementPage.js"),
);
const { ProductHierarchyPage } = lazy(
	() => import("../features/data-perspectives/pages/ProductHierarchyPage.js"),
);
const { ProjectHierarchyPage } = lazy(
	() => import("../features/data-perspectives/pages/ProjectHierarchyPage.js"),
);
const { SupplierHierarchyPage } = lazy(
	() => import("../features/data-perspectives/pages/SupplierHierarchyPage.js"),
);
const { HierarchiesV2Page } = lazy(
	() => import("../features/hierarchies-v2/pages/HierarchiesV2Page.js"),
);
const { HierarchyEditPage } = lazy(
	() => import("../features/hierarchies-v2/pages/HierarchyEditPage.js"),
);
const { CreateHierarchyV2Page } = lazy(
	() => import("../features/hierarchies-v2/pages/CreateHierarchyV2Page.js"),
);
const { HierarchyHistoryPage } = lazy(
	() => import("../features/hierarchies-v2/pages/HierarchyHistoryPage.js"),
);
const { HierarchiesEntityRedirect } = lazy(
	() => import("../features/hierarchies-v2/pages/HierarchiesEntityRedirect.js"),
);
const { LegalEntityInfoPage } = lazy(
	() => import("../features/legalEntities/infoPage/LegalEntityInfoPage.js"),
);
const { CreatePlanPage } = lazy(
	() => import("../features/planning/plans/CreatePlanPage/CreatePlanPage.js"),
);
const { PlanDetailLayout } = lazy(
	() => import("../features/planning/PlanDetailLayout.js"),
);
const { PlanDetailPage } = lazy(
	() => import("../features/planning/tasks/TaskDetailPage/PlanDetailPage.js"),
);
const { PlanningOverviewPage } = lazy(
	() => import("../features/planning/plans/PlanningOverviewPage.js"),
);
const { AssumptionsTab } = lazy(
	() => import("../features/planning/tasks/TaskDetailPage/AssumptionsTab.js"),
);
const { DefinitionsTabs } = lazy(
	() => import("../features/planning/tasks/TaskDetailPage/DefinitionsTab.js"),
);
const { AssetRegisters } = lazy(
	() => import("../features/planning/modules/assets/AssetRegisters.js"),
);
const { FinanceRegisters } = lazy(
	() => import("../features/planning/modules/finance/FinanceRegisters.js"),
);
const { FinancialAssetTypes } = lazy(
	() =>
		import(
			"../features/planning/modules/finance/financialAssetType/FinancialAssetTypes.js"
		),
);
const { EmployeeGroupPage } = lazy(
	() =>
		import(
			"../features/planning/modules/employee/register/employeeGroups/EmployeeGroupPage.js"
		),
);
const { ConsultancyRegisters } = lazy(
	() =>
		import(
			"../features/planning/modules/consultancy/register/ConsultancyRegisters.js"
		),
);
const { ConsultantTypes } = lazy(
	() =>
		import(
			"../features/planning/modules/consultancy/register/consultant-types/ConsultantTypes.js"
		),
);
const { CustomerInvoicePage } = lazy(
	() => import("src/features/invoice/CustomerInvoicePage.js"),
);
const { SupplierInvoicePage } = lazy(
	() => import("src/features/invoice/SupplierInvoicePage.js"),
);
const { CopyPlanPage } = lazy(
	() => import("../features/planning/plans/CopyPlanPage.js"),
);
const { PlanningSettingsPage } = lazy(
	() => import("../features/planning/settings/PlanningSettingsPage.js"),
);
const { HolidaysPage } = lazy(
	() =>
		import("../features/planning/settings/general/holidays/HolidaysPage.js"),
);
const { CostCenters } = lazy(
	() => import("../features/planning/settings/dim/costCenters/CostCenters.js"),
);
const { Customers } = lazy(
	() => import("../features/planning/settings/dim/Customers.js"),
);
const { Products } = lazy(
	() => import("../features/planning/settings/dim/Products.js"),
);
const { Projects } = lazy(
	() => import("../features/planning/settings/dim/Projects.js"),
);
const { Suppliers } = lazy(
	() => import("../features/planning/settings/dim/Suppliers.js"),
);
const { Dimensions } = lazy(
	() => import("../features/planning/registers/Dimensions.js"),
);
const { DebitRateKeys } = lazy(
	() =>
		import(
			"../features/planning/modules/consultancy/register/debit-rate-keys/DebitRateKeys.js"
		),
);
const { AccountLinks } = lazy(
	() => import("../features/planning/registers/accountLink/AccountLinks"),
);
const { AssetTypes } = lazy(
	() => import("../features/planning/modules/assets/assetType/AssetTypes.js"),
);
const { DriversRegisterPage } = lazy(
	() =>
		import("../features/planning/modules/driver/registers/DriversRegisterPage"),
);
const { PersonnelAgreements } = lazy(
	() =>
		import(
			"../features/planning/modules/employee/register/personnelAgreement/PersonnelAgreements"
		),
);
const { VacationKeys } = lazy(
	() =>
		import(
			"../features/planning/modules/employee/register/vacationKeys/VacationKeys"
		),
);
const { ServiceLevelKeys } = lazy(
	() =>
		import(
			"../features/planning/modules/employee/register/serviceLevelKeys/ServiceLevelKeys"
		),
);
const { VatSettings } = lazy(
	() =>
		import("../features/planning/settings/general/vatSetting/VatSettings.js"),
);

const { AllocationKeys } = lazy(
	() => import("../features/planning/registers/allocationKeys/AllocationKeys"),
);
const { AccountRecipes } = lazy(
	() => import("../features/planning/registers/accountRecipes/AccountRecipes"),
);
const { AssetPage } = lazy(
	() => import("../features/planning/modules/assets/assetType/AssetPage.js"),
);
const { FinancialAssetPage } = lazy(
	() =>
		import(
			"../features/planning/modules/finance/financialAssetType/FinancialAssetPage.js"
		),
);
const { EmployeeRegisters } = lazy(
	() =>
		import(
			"../features/planning/modules/employee/register/EmployeeRegisters.js"
		),
);
const { EmployeePage } = lazy(
	() =>
		import("../features/planning/modules/employee/register/EmployeePage.js"),
);

const { DataLayerWrapper } = lazy(
	() => import("../features/dataLayer/DataLayerWrapper"),
);
const { IntelligenceLayerOverview } = lazy(
	() => import("../features/dataLayer/IntelligenceLayerOverview"),
);

const { DataLayerPage } = lazy(
	() => import("../features/dataLayer/DataLayerPage"),
);

const { Integrations } = lazy(
	() => import("../features/integrations/overview/Integrations.js"),
);
const { IntegrationsOverviewPage } = lazy(
	() =>
		import(
			"../features/integrations/overview/table/IntegrationsOverviewPage.js"
		),
);
const { AddIntegration } = lazy(
	() => import("../features/integrations/sources/AddIntegration.js"),
);
const { UsersAndGroupsPage } = lazy(
	() => import("../features/usersAndGroups/UsersAndGroupsPage"),
);
const { SupportUsers } = lazy(
	() => import("../features/supportUsers/SupportUsers"),
);
const { AccountTypeEditPage } = lazy(
	() => import("../features/data-perspectives/pages/AccountTypeEditPage.js"),
);
const { VitecIntegrationOverview } = lazy(
	() =>
		import(
			"../features/integrations/sources/vitec/VitecIntegrationOverview.js"
		),
);
const { SiePreviewPage } = lazy(
	() => import("../features/integrations/sources/sie/SiePreviewPage.js"),
);
const { SieIntegrationPage } = lazy(
	() => import("../features/integrations/sources/sie/integration/IndexPage.js"),
);
const { SieIntegrationFileSummaryPage } = lazy(
	() =>
		import("../features/integrations/sources/sie/integration/SummaryPage.js"),
);
const { AiChatPage } = lazy(
	() => import("../features/ai-chat/components/AiChatPage.js"),
);
const { AiChatIndexRedirect } = lazy(
	() => import("../features/ai-chat/components/AiChatIndexRedirect.js"),
);
const { AiChatProductPage } = lazy(
	() => import("../features/ai-chat/components/AiChatProductPage.js"),
);

const AiChatAccessRoute = ({
	children,
	fallback,
}: {
	children: React.ReactNode;
	fallback?: React.ReactNode;
}) => {
	const companyDomain = useLoadedCompanyDomain();
	const { hasAccess, optedIn, isLoading } = useAiChatAccess(companyDomain.id);
	if (!hasAccess) {
		return <Navigate to={`/company-domains/${companyDomain.id}`} replace />;
	}
	if (isLoading) return null;
	if (!optedIn)
		return (
			fallback ?? (
				<Navigate to={`/company-domains/${companyDomain.id}`} replace />
			)
		);
	return children;
};

const dataSourcesLoaderFn = createLoaderWithAuthCheck(
	async (args, handlerCtx) => {
		await Promise.all([
			subscribeToQuery({
				getThunkAction(args) {
					return fusionHubApi.endpoints.getAllIntegrations.initiate(
						{
							companyDomainId: args.params.companyDomainId as string,
						},
						{ subscriptionOptions: { pollingInterval: 1000 * 10 } },
					);
				},
				args,
				handlerCtx,
			}),
			subscribeToQuery({
				getThunkAction(args) {
					return nexusApi.endpoints.getIntegrations.initiate({
						companyDomainId: args.params.companyDomainId as string,
					});
				},
				args,
				handlerCtx,
			}),
			subscribeToQuery({
				getThunkAction() {
					return fortnoxApi.endpoints.getStatus.initiate(undefined, {
						subscriptionOptions: {
							pollingInterval: 1000 * 60,
						},
					});
				},
				args,
				handlerCtx,
			}),
			subscribeToQuery({
				getThunkAction() {
					return fusionHubApi.endpoints.getErAdapters.initiate();
				},
				args,
				handlerCtx,
			}),
		]);
	},
);

const dataSourceDetailLoaderFn = createLoaderWithAuthCheck(
	async (args, handlerCtx) => {
		if (!Number.isFinite(Number(args.params.integrationId))) {
			throw data(null, { status: 404 });
		}
		await subscribeToQuery({
			getThunkAction(args) {
				return fusionHubApi.endpoints.getIntegration.initiate(
					{
						integrationId: Number(args.params.integrationId),
					},
					{ subscriptionOptions: { pollingInterval: 1000 * 10 } },
				);
			},
			args,
			handlerCtx,
			getErrorToThrow() {
				return data(null, { status: 404 });
			},
		});
	},
);

// Mounted at both domain-wide and per-entity scope, reused by reference so the two trees can't drift.
const dataSourcesRoute: RouteObject = {
	path: "data-sources",
	children: [
		{
			element: <Integrations />,
			children: [
				{
					index: true,
					element: <IntegrationsOverviewPage />,
					loader: dataSourcesLoaderFn,
				},
				{
					path: "documentation",
					element: <Documentation />,
				},
				{
					path: "data-runs",
					children: [
						{ index: true, element: <JobListingPage /> },
						{ path: ":dataRunId", element: <JobDetailPage /> },
					],
				},
			],
		},
		{
			path: ":integrationId",
			element: <IntegrationDetailPage />,
			loader: dataSourceDetailLoaderFn,
		},
		{
			path: "setup",
			element: <ConnectIntegrationPage />,
		},
		{
			path: "new",
			children: [
				{ index: true, element: <AddIntegration /> },
				{
					path: "vitec",
					element: <VitecIntegrationOverview />,
				},
				{
					path: "sie",
					children: [
						{
							path: ":sourceId",
							element: <SiePreviewPage />,
						},
					],
				},
			],
		},
		{
			path: "sie/:integrationId",
			children: [
				{
					index: true,
					element: <SieIntegrationPage />,
				},
				{
					path: ":fileId",
					element: <SieIntegrationFileSummaryPage />,
				},
			],
		},
	],
};

// Same reuse-by-reference as dataSourcesRoute above.
const planningRoute: RouteObject = {
	path: "planning",
	element: <PlanPageWrapper />,
	children: [
		{
			path: "",
			element: <PlanningOverviewPage />,
		},
		{ path: "create-plan", element: <CreatePlanPage /> },
	],
};

// Segments the sidebar's scope switcher (scopedFeaturePaths.ts) can't gate-check at a new
// scope, so it falls back to that scope's root for these instead of risking an unauthorized page.
export const GATED_FEATURE_SEGMENTS = new Set([
	"settings",
	"intelligence-layer",
	"permissions",
	"internal",
]);

// Avoids an import cycle: router.tsx -> CompanyRouteLayout -> CompanySidebarPart -> CompanySelect -> router.js.
const CompanyRouteTreeProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => (
	<RouteTreeProvider
		value={{ routeObjects, gatedFeatureSegments: GATED_FEATURE_SEGMENTS }}
	>
		{children}
	</RouteTreeProvider>
);

// Exported so tests can drive it via createMemoryRouter instead of the createBrowserRouter singleton below.
export const routeObjects: RouteObject[] = [
	{
		path: "*",
		element: <PageNotFound />,
	},
	{
		path: "fortnox-external",
		element: <AddFortnoxIntegrationExternalRedirect />,
	},
	{
		path: "fortnox-external-callback",
		element: <FortnoxExternalCallbackPage />,
	},
	{
		path: "exact-online-external",
		element: <AddExactOnlineIntegrationExternalRedirect />,
	},
	{
		path: "exact-online-external-callback",
		element: <ExactOnlineExternalCallbackPage />,
	},
	{
		path: "poweroffice-external-callback",
		element: <PowerofficeExternalCallbackPage />,
	},
	{
		path: "datev-external-callback",
		element: <DatevExternalCallbackPage />,
	},
	{
		path: "visma-eaccounting-external-callback",
		element: <VismaEaccountingExternalCallbackPage />,
	},
	{
		path: "visma-economic-external-callback",
		element: <VismaEconomicExternalCallbackPage />,
	},
	{
		path: "visma-dinero-external-callback",
		element: <VismaDineroExternalCallbackPage />,
	},
	{
		path: "verify-email",
		element: <EmailVerification />,
	},
	{
		path: "/",
		loader: createLoaderWithAuthCheck(
			async (args, handlerCtx) => {
				// Unawaited: just warms the profile service's Azure Function early; Home.tsx never depends on it.
				if (isUserProfileServiceConfigured) {
					void store.dispatch(userProfileApi.endpoints.getProfile.initiate());
				}

				await Promise.all([
					subscribeToQuery({
						getThunkAction() {
							return exopenApi.endpoints.getAllCompanies.initiate();
						},
						args,
						handlerCtx,
					}),
					subscribeToQuery({
						getThunkAction() {
							return exopenApi.endpoints.getCurrentUser.initiate();
						},
						args,
						handlerCtx,
					}),
				]);
			},
			{ performAuthentication: true },
		),
		element: <ProtectedApp />,
		children: [
			{ path: "fortnox", element: <ActivateFortnoxPage /> },
			{ path: "exact-online", element: <ActivateExactOnlinePage /> },
			{
				path: "company-domains/:companyDomainId",
				element: (
					<CompanyRouteTreeProvider>
						<CompanyRouteLayout />
					</CompanyRouteTreeProvider>
				),
				loader: createLoaderWithAuthCheck(async (args, handlerCtx) => {
					await Promise.all([
						subscribeToQuery({
							getThunkAction(args) {
								return exopenApi.endpoints.getCompany.initiate({
									companyId: args.params.companyDomainId as string,
								});
							},
							args,
							handlerCtx,
							getErrorToThrow() {
								return data(null, { status: 404 });
							},
						}),
						subscribeToQuery({
							getThunkAction(args) {
								return nexusApi.endpoints.getLegalEntities.initiate(
									args.params.companyDomainId as string,
								);
							},
							args,
							handlerCtx,
							getErrorToThrow() {
								return data(null, { status: 404 });
							},
						}),
						subscribeToQuery({
							getThunkAction(args) {
								return nexusApi.endpoints.getCompanyHierarchy.initiate(
									args.params.companyDomainId as string,
								);
							},
							args,
							handlerCtx,
							getErrorToThrow() {
								return data(null, { status: 404 });
							},
						}),
					]);
				}),
				children: [
					{ index: true, element: <StartDashboard /> },
					dataSourcesRoute,
					planningRoute,
					// Moved under legal-entities as a tab (portal#4304); redirect for old bookmarks.
					{
						path: "hierarchy",
						element: <Navigate to="../legal-entities/hierarchy" replace />,
					},
					// Old name kept as a redirect — this URL was in active use (bookmarks/deep links).
					{
						path: "datalayer",
						element: <Navigate to="../intelligence-layer" replace />,
					},
					{
						path: "intelligence-layer",
						element: (
							<AuthRoute
								Component={DataLayerWrapper}
								permissions={[COMPANYADMIN, "DataLayer"]}
								permissionMode="any"
							/>
						),
						children: [
							{
								path: "",
								element: <IntelligenceLayerOverview />,
								children: [
									{
										index: true,
										element: <DataLayerPage />,
									},
								],
							},
						],
					},
					// The path is getProduct("excel").path; the page redirects users without v7 access.
					{ path: "excel-reports", element: <ExcelReportsPage /> },
					{ path: "powerbi-reports", element: <PowerBiReportsPage /> },
					{
						path: "ai-chat",
						element: (
							<AiChatAccessRoute fallback={<AiChatProductPage />}>
								<Outlet />
							</AiChatAccessRoute>
						),
						children: [
							{
								index: true,
								element: <AiChatIndexRedirect />,
							},
							{
								path: "conversations",
								children: [
									{
										path: "new",
										element: <AiChatPage />,
									},
									{
										path: ":conversationId",
										element: <AiChatPage />,
									},
								],
							},
						],
					},
					{
						path: "legal-entities",
						children: [
							{
								element: <LegalEntitiesLayout />,
								children: [
									{ index: true, element: <LegalEntitiesPage /> },
									{ path: "hierarchy", element: <GroupHierarchyCard /> },
								],
							},
							{
								path: ":legalEntityId",
								loader: createLoaderWithAuthCheck(async (args, handlerCtx) => {
									await Promise.all([
										subscribeToQuery({
											getThunkAction(args) {
												return nexusApi.endpoints.getLegalEntity.initiate({
													legalEntityId: args.params.legalEntityId as string,
													companyDomainId: args.params
														.companyDomainId as string,
												});
											},
											args,
											handlerCtx,
											// WHY redirect, not 404: a company the user may not open is a scope to drop, not a dead end.
											getErrorToThrow(error) {
												// Only 403/404 mean a wrong scope; a 5xx or network failure must not read as "no access".
												if (!isMissingOrForbidden(error)) {
													return error;
												}
												showErrorNotification({
													message: i18n.t(
														"You do not have access to that company, or it no longer exists",
													),
												});
												return redirect(
													`/company-domains/${args.params.companyDomainId}`,
												);
											},
										}),
									]);
								}),
								children: [
									{ index: true, element: <StartDashboard /> },
									{
										element: <LegalEntityDetailLayout />,
										children: [
											{
												element: <AccountHierarchyLayout />,
												path: "accounts",
												children: [
													{
														index: true,
														element: <IncomeStatementHierarchyPage />,
													},
													{
														path: "balance-sheet",
														element: <BalanceSheetHierarchyPage />,
													},
													{
														path: "types",
														element: <AccountTypeEditPage />,
													},
												],
											},
											{
												path: "projects",
												element: <ProjectHierarchyPage />,
											},
											{
												path: "cost-centers",
												element: <CostCenterHierarchyPage />,
											},
											{
												path: "products",
												element: <ProductHierarchyPage />,
											},
											{
												path: "customers",
												element: <CustomerHierarchyPage />,
											},
											{
												path: "suppliers",
												element: <SupplierHierarchyPage />,
											},
											{
												path: "info",
												element: <LegalEntityInfoPage />,
											},
											{
												path: "invoices",
												element: <InvoicesLayout />,
												children: [
													{
														index: true,
														element: <Navigate to="customer" />,
													},
													{
														path: "customer",
														children: [
															{
																index: true,
																element: <CustomerInvoicesPage />,
															},
															{
																path: ":invoiceId",
																element: <CustomerInvoicePage />,
															},
														],
													},
													{
														path: "supplier",
														children: [
															{
																index: true,
																element: <SupplierInvoicesPage />,
															},
															{
																path: ":invoiceId",
																element: <SupplierInvoicePage />,
															},
														],
													},
												],
											},
											{
												path: "customer-invoices/:invoiceId",
												Component: () => {
													const { invoiceId } = useParams();
													return (
														<Navigate
															to={`../invoices/customer/${invoiceId}`}
														/>
													);
												},
											},
											{
												path: "supplier-invoices/:invoiceId",
												Component: () => {
													const { invoiceId } = useParams();
													return (
														<Navigate
															to={`../invoices/supplier/${invoiceId}`}
														/>
													);
												},
											},
										],
									},

									{
										path: "hierarchies",
										children: [
											{ index: true, element: <HierarchiesV2Page /> },
											{ path: "new", element: <CreateHierarchyV2Page /> },
											{
												path: ":hierarchyId",
												element: <HierarchyEditPage />,
											},
											{
												path: ":hierarchyId/history",
												element: <HierarchyHistoryPage />,
											},
										],
									},
									dataSourcesRoute,
									planningRoute,
									{
										path: "plan/:planId",
										element: <PlanDetailLayout />,
										children: [
											{
												element: <PlanDetailPage />,
												children: [
													{ index: true, element: <AssumptionsTab /> },
													{
														path: "definitions",
														element: <DefinitionsTabs />,
														children: [
															{
																path: "account-recipes",
																element: <AccountRecipes />,
															},
															{
																path: "account",
																element: <AccountLinks />,
																id: "account",
															},
															{
																path: "accrual-keys",
																element: <AllocationKeys />,
															},
															{
																path: "incoming-balance",
																element: <IncomingBalancePage />,
															},
															{
																path: "driver",
																element: (
																	<DriversRegisterPage refType="driver" />
																),
																id: "driver",
															},
															{
																path: "asset",
																element: <AssetRegisters />,
																children: [
																	{
																		index: true,
																		element: <AssetTypes />,
																	},
																	{
																		path: "asset",
																		element: <AssetPage />,
																	},
																],
																id: "asset",
															},
															{
																path: "finance",
																element: <FinanceRegisters />,
																children: [
																	{
																		index: true,
																		element: <FinancialAssetTypes />,
																	},
																	{
																		path: "financial-asset",
																		element: <FinancialAssetPage />,
																	},
																],
																id: "finance",
															},
															{
																path: "employee_pp",
																element: <EmployeeRegisters />,
																children: [
																	{
																		index: true,
																		element: <EmployeePage />,
																	},
																	{
																		path: "personnel-agreements",
																		element: <PersonnelAgreements />,
																	},
																	{
																		path: "benefits",
																		element: (
																			<DriversRegisterPage refType="benefit" />
																		),
																	},
																	{
																		path: "vacation-keys",
																		element: <VacationKeys />,
																	},
																	{
																		path: "service-level-keys",
																		element: <ServiceLevelKeys />,
																	},
																	{
																		path: "employee-groups",
																		element: <EmployeeGroupPage />,
																	},
																],
																id: "employee_pp",
															},
															{
																path: "consultancy",
																element: <ConsultancyRegisters />,
																children: [
																	{
																		index: true,
																		element: <ConsultantTypes />,
																	},
																	{
																		path: "debit-rate-keys",
																		element: <DebitRateKeys />,
																	},
																],
																id: "consultancy",
															},
														],
														id: PLANNING_DEFINITIONS_TAB_ID,
													},
												],
											},

											{ path: "copy-plan", element: <CopyPlanPage /> },
											{
												path: "settings",
												element: <PlanningSettingsPage />,
												children: [
													{
														path: "general",
														element: <GeneralSettingsPage />,
													},
													{ path: "users", element: <PlanningUsersPage /> },
													{
														path: "dimensions",
														element: <Dimensions />,
														children: [
															{
																index: true,
																element: <CostCenters />,
																loader: createLoaderWithAuthCheck(
																	async (args, handlerCtx) => {
																		await Promise.all([
																			subscribeToQuery({
																				getThunkAction(args) {
																					return planningApi.endpoints.getOrganizationalUnit.initiate(
																						{
																							planId: args.params
																								.planId as string,
																							companyDomainId: args.params
																								.companyDomainId as string,
																						},
																					);
																				},
																				args,
																				handlerCtx,
																			}),
																		]);
																	},
																),
															},
															{ path: "customers", element: <Customers /> },
															{ path: "suppliers", element: <Suppliers /> },
															{ path: "projects", element: <Projects /> },
															{ path: "products", element: <Products /> },
														],
													},
													{
														path: "vat",
														element: <VatSettings />,
													},
													{
														path: "holidays",
														element: <HolidaysPage />,
													},
												],
											},
										],
									},
								],
							},
						],
					},
					{
						path: "permissions",
						element: (
							<AuthRoute
								Component={PermissionsPage}
								permissions={[SUPERADMIN]}
							/>
						),
					},
					{
						path: "hierarchies/*",
						element: <HierarchiesEntityRedirect />,
					},
					{
						path: "settings",
						element: (
							<AuthRoute
								Component={SettingsPage}
								permissions={[COMPANYADMIN]}
							/>
						),
						children: [
							{ index: true, element: <CompanyDomainSettingsPage /> },
							{
								path: "users",
								element: <UsersAndGroupsPage />,
								children: [
									{ index: true, element: <UserTable /> },
									{ path: "groups", element: <AccessGroupTable /> },
								],
							},
							{
								path: "terms-and-policies",
								element: <TermsAndPoliciesPage />,
							},
							{
								path: "api-keys",
								element: <ApiKeysSettingsPage />,
							},
							{
								path: "ai-chat",
								element: (
									<AiChatAccessRoute>
										<AiChatSettingsPage />
									</AiChatAccessRoute>
								),
							},
							{
								path: "planning",
								element: <PlanningModulesSettingsPage />,
							},
						],
					},
					{
						path: "internal",
						children: [
							{
								path: "dev-tools",
								element: (
									<AuthRoute
										Component={DevToolsPage}
										permissions={[SUPERADMIN]}
									/>
								),
								children: [
									{ index: true, element: <DevToolsIndex /> },
									{
										element: <DevToolsSubPageLayout />,
										children: [
											{ path: "support", element: <SupportTools /> },
											{
												path: "integrations",
												element: <DevtoolIntegrationsPage />,
												loader: dataSourcesLoaderFn,
											},
											{
												path: "nexus-syncs",
												element: <NexusSyncsPage />,
												loader: dataSourcesLoaderFn,
											},
											{ path: "legal-entities", element: <LegalEntities /> },
											{ path: "planning", element: <PlanningAdminCP /> },
											{ path: "plans", element: <AdminPlansPage /> },
											{
												path: "azure-resources",
												element: <AzureResourceTools />,
											},
											{ path: "dlm", element: <DLMTools /> },
											{ path: "template-apps", element: <TemplateApps /> },
											{ path: "reports", element: <Reports /> },
											{ path: "users", element: <SupportUsers /> },
											{
												path: "permissions",
												element: <PermissionDefinitionsDevPage />,
											},
											{
												path: "integration-status",
												element: <FleetIntegrationStatusPage />,
											},
											{
												path: "instance-memory",
												element: <InstanceMemoryPage />,
											},
										],
									},
								],
							},
						],
					},
				],
			},
			{
				path: ":companyId",
				Component: () => {
					const { companyId } = useParams();
					return <Navigate to={`/company-domains/${companyId}`} />;
				},
			},
			{
				path: "vismanet-redirect",
				element: (
					<AppFrame>
						<MainFrame>
							<AddVismaNetIntegrationRedirect />
						</MainFrame>
					</AppFrame>
				),
			},
			{
				element: (
					<AppFrame>
						<HomeSidebar />
						<MainFrame>
							<Outlet />
						</MainFrame>
					</AppFrame>
				),
				children: [
					{
						index: true,
						element: <Home />,
					},
				],
			},
		],
		HydrateFallback: () => (
			<div className="grid place-items-center h-screen">
				<CircleLoader />
			</div>
		),
		ErrorBoundary: () => {
			const error = useRouteError();
			if (isRouteErrorResponse(error) && error.status === 404) {
				return <PageNotFound />;
			}

			const message = "Oops!";
			const details = "An unexpected error occurred.";
			let stack: string | undefined;

			return (
				<main>
					<h1>{message}</h1>
					<p>{details}</p>
					{stack && (
						<pre>
							<code>{stack}</code>
						</pre>
					)}
				</main>
			);
		},
	},
];

ensureRoutesHaveLoaderFunction(routeObjects);

export const router = createBrowserRouter(routeObjects, {
	dataStrategy,
});

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		dispatchUnloadEvents([]);
		router.dispose();
	});
}
